import { useEffect, useMemo, useState } from "react";
import {
  Plus, Trash2, Save, X, Home, Users, Zap, Wifi, Building2, Package, MoreHorizontal,
  TrendingUp, TrendingDown, DollarSign, Calendar, FileText, Wrench, Boxes,
} from "lucide-react";
import api from "../lib/api";
import { fmtBRL, PAY_LABELS, PAY_COLORS, monthLabel } from "../lib/constants";
import { useAuth } from "../lib/auth";
import { exportFinancialReport } from "../lib/financialReport";
import { exportPartnershipReport } from "../lib/partnershipReport";
import ConfirmDialog from "../components/ConfirmDialog";
import PartnershipModal from "../components/PartnershipModal";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const dateToNum = (br) => {
  if (!br) return 0;
  const [d, m, y] = br.split("/");
  return (parseInt(y) || 0) * 10000 + (parseInt(m) || 0) * 100 + (parseInt(d) || 0);
};
function orderMonth(o) { return o.exitDate?.slice(3, 10); }
function saleMonth(s) {
  const parts = (s.date || "").split("/");
  return parts.length >= 2 ? parts.slice(1).join("/") : "";
}
function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}
function firstOfMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function isoToDateNum(iso) {
  if (!iso) return 0;
  const [y, m, d] = iso.split("-");
  return parseInt(y) * 10000 + parseInt(m) * 100 + parseInt(d);
}

const CAT_INFO = {
  condominio: { label: "Condomínio", icon: Building2, color: "#7c3aed" },
  funcionario: { label: "Funcionário", icon: Users, color: "#0891b2" },
  aluguel: { label: "Aluguel", icon: Home, color: "#dc2626" },
  energia: { label: "Energia", icon: Zap, color: "#eab308" },
  internet: { label: "Internet", icon: Wifi, color: "#2563eb" },
  material: { label: "Material", icon: Package, color: "#059669" },
  outros: { label: "Outros", icon: MoreHorizontal, color: "#64748b" },
};

// ─── View ─────────────────────────────────────────────────────────────────────
export default function FinanceiroView({ orders, pieceSales }) {
  const { user } = useAuth();
  const canSeeCost = user?.role === "admin";
  const [showPartnership, setShowPartnership] = useState(false);
  const [mode, setMode] = useState("month"); // "month" | "custom"
  const [monthFilter, setMonthFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());

  const [expenses, setExpenses] = useState([]);
  const [loadingExp, setLoadingExp] = useState(true);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingExp, setEditingExp] = useState(null);
  const [confirmDeleteExp, setConfirmDeleteExp] = useState(null);

  const refreshExpenses = async () => {
    try {
      const r = await api.get("/expenses");
      setExpenses(r.data);
    } finally {
      setLoadingExp(false);
    }
  };
  useEffect(() => { refreshExpenses(); }, []);

  const deliveredAll = useMemo(() => orders.filter((o) => o.status === "delivered" && !!o.exitDate), [orders]);

  const availableMonths = useMemo(() => {
    const seen = new Set();
    deliveredAll.forEach((o) => { const m = orderMonth(o); if (m) seen.add(m); });
    pieceSales.forEach((s) => { const m = saleMonth(s); if (m && m.length > 5) seen.add(m); });
    expenses.forEach((e) => { if (e.monthRef) seen.add(e.monthRef); });
    return Array.from(seen).sort();
  }, [deliveredAll, pieceSales, expenses]);

  // Filtering
  const inFilter = useMemo(() => {
    if (mode === "month") {
      return {
        orders: monthFilter === "all" ? deliveredAll : deliveredAll.filter((o) => orderMonth(o) === monthFilter),
        sales: monthFilter === "all" ? pieceSales : pieceSales.filter((s) => saleMonth(s) === monthFilter || saleMonth(s) === monthFilter.split("/")[0]),
        expenses: monthFilter === "all" ? expenses : expenses.filter((e) => e.recurring || e.monthRef === monthFilter),
      };
    }
    // custom range
    const from = isoToDateNum(dateFrom);
    const to = isoToDateNum(dateTo);
    const inRangeBR = (br) => {
      const [d, m, y] = (br || "").split("/");
      if (!d || !m || !y) return false;
      const n = parseInt(y) * 10000 + parseInt(m) * 100 + parseInt(d);
      return n >= from && n <= to;
    };
    return {
      orders: deliveredAll.filter((o) => inRangeBR(o.exitDate)),
      sales: pieceSales.filter((s) => {
        // sales may store dd/mm or dd/mm/yyyy — assume current year if short
        let d = s.date;
        if (d && d.length <= 5) {
          d = `${d}/${new Date().getFullYear()}`;
        }
        return inRangeBR(d);
      }),
      // for expenses: include recurring always, plus one-time within same period (by monthRef intersection)
      expenses: expenses.filter((e) => {
        if (e.recurring) return true;
        if (!e.monthRef) return false;
        const [mm, yy] = e.monthRef.split("/");
        const first = parseInt(yy) * 10000 + parseInt(mm) * 100 + 1;
        const last = parseInt(yy) * 10000 + parseInt(mm) * 100 + 31;
        return last >= from && first <= to;
      }),
    };
  }, [mode, monthFilter, dateFrom, dateTo, deliveredAll, pieceSales, expenses]);

  const totals = useMemo(() => {
    // Net receita = total - desconto (mantém consistência com o valor efetivamente recebido)
    const receitaOS = inFilter.orders.reduce((s, o) => s + (Number(o.total) || 0) - (Number(o.discount) || 0), 0);
    const receitaPeca = inFilter.sales.reduce((s, x) => s + (Number(x.value) || 0) - (Number(x.discount) || 0), 0);
    const receita = receitaOS + receitaPeca;
    const custoPeca = inFilter.orders.reduce((s, o) => s + (Number(o.costValue) || 0), 0);
    const despesas = inFilter.expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const totalCustos = custoPeca + despesas;
    const lucro = receita - totalCustos;
    const margem = receita > 0 ? (lucro / receita) * 100 : 0;

    const byPay = {};
    inFilter.orders.forEach((o) => {
      if (!o.payMethod) return;
      byPay[o.payMethod] = (byPay[o.payMethod] || 0) + Math.max(0, (Number(o.total) || 0) - (Number(o.discount) || 0));
    });
    inFilter.sales.forEach((s) => {
      if (!s.payMethod) return;
      byPay[s.payMethod] = (byPay[s.payMethod] || 0) + Math.max(0, (Number(s.value) || 0) - (Number(s.discount) || 0));
    });

    const byCategory = {};
    inFilter.expenses.forEach((e) => {
      byCategory[e.category] = (byCategory[e.category] || 0) + (Number(e.amount) || 0);
    });

    return { receitaOS, receitaPeca, receita, custoPeca, despesas, totalCustos, lucro, margem, byPay, byCategory };
  }, [inFilter]);

  const periodLabel = mode === "month"
    ? (monthFilter === "all" ? "Todo o período" : monthLabel(monthFilter))
    : `${dateFrom.split("-").reverse().join("/")} → ${dateTo.split("-").reverse().join("/")}`;

  return (
    <div className="p-6 space-y-4" data-testid="financeiro-view">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Financeiro</h2>
          <p className="text-sm text-slate-500 mt-0.5">Período: <b>{periodLabel}</b></p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportFinancialReport({
              periodLabel,
              orders: inFilter.orders,
              sales: inFilter.sales,
              expenses: inFilter.expenses,
              totals,
              canSeeCost,
              storeName: (typeof window !== "undefined" && window.__storeName__) || "Phone Lab",
            })}
            data-testid="fin-export-report"
            className="h-9 px-4 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1.5"
          >
            <FileText className="w-3.5 h-3.5" /> Exportar Relatório
          </button>
          {canSeeCost && (
            <button
              onClick={() => setShowPartnership(true)}
              data-testid="fin-partnership-btn"
              className="h-9 px-4 rounded-lg text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white inline-flex items-center gap-1.5"
              title="Fechamento entre sócios"
            >
              <Users className="w-3.5 h-3.5" /> Fechamento entre sócios
            </button>
          )}
          <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
            <button
              onClick={() => setMode("month")}
              data-testid="fin-mode-month"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${mode === "month" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
            >
              Por mês
            </button>
            <button
              onClick={() => setMode("custom")}
              data-testid="fin-mode-custom"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${mode === "custom" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
            >
              Período customizado
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {mode === "month" ? (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setMonthFilter("all")}
            data-testid="fin-month-all"
            className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
              monthFilter === "all" ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
            }`}
          >
            Todos os meses
          </button>
          {availableMonths.map((m) => (
            <button
              key={m}
              onClick={() => setMonthFilter(m)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                monthFilter === m ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
              }`}
            >
              {m.length > 5 ? monthLabel(m) : m}
            </button>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider block mb-1">De</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="fin-date-from"
              className="h-9 rounded-md border border-slate-300 px-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider block mb-1">Até</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="fin-date-to"
              className="h-9 rounded-md border border-slate-300 px-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div className="flex gap-1">
            {[
              ["Hoje", () => { const t = todayISO(); setDateFrom(t); setDateTo(t); }],
              ["7 dias", () => { const t = new Date(); const s = new Date(); s.setDate(t.getDate() - 6); setDateFrom(s.toISOString().slice(0, 10)); setDateTo(t.toISOString().slice(0, 10)); }],
              ["30 dias", () => { const t = new Date(); const s = new Date(); s.setDate(t.getDate() - 29); setDateFrom(s.toISOString().slice(0, 10)); setDateTo(t.toISOString().slice(0, 10)); }],
              ["Mês", () => { setDateFrom(firstOfMonthISO()); setDateTo(todayISO()); }],
            ].map(([lbl, fn]) => (
              <button
                key={lbl}
                onClick={fn}
                className="h-9 px-3 rounded-md text-xs font-semibold border border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-700"
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className={`grid gap-3 ${canSeeCost ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-3"}`}>
        <KpiCard label="Receita" value={totals.receita} icon={TrendingUp} tone="blue" />
        {canSeeCost && (
          <KpiCard label="Custo de peças" value={totals.custoPeca} icon={Package} tone="rose" />
        )}
        <KpiCard label="Despesas fixas" value={totals.despesas} icon={TrendingDown} tone="amber" />
        {canSeeCost ? (
          <KpiCard
            label="Lucro líquido"
            value={totals.lucro}
            hint={`${totals.margem.toFixed(1)}% margem`}
            icon={DollarSign}
            tone={totals.lucro >= 0 ? "emerald" : "rose"}
            primary
          />
        ) : (
          <KpiCard
            label="Receita - Despesas"
            value={totals.receita - totals.despesas}
            icon={DollarSign}
            tone={(totals.receita - totals.despesas) >= 0 ? "emerald" : "rose"}
            primary
          />
        )}
      </div>

      {/* Breakdown Serviços vs Peças (admin only) */}
      {canSeeCost && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <KpiCard label="Faturamento — Serviços" value={totals.receitaOS} hint={`${inFilter.orders.length} OS`} icon={Wrench} tone="blue" />
          <KpiCard label="Faturamento — Peças" value={totals.receitaPeca} hint={`${inFilter.sales.length} venda(s)`} icon={Boxes} tone="violet" />
          <KpiCard label="Total geral" value={totals.receita} icon={DollarSign} tone="emerald" primary />
        </div>
      )}

      {/* Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Receita detalhada</h3>
          <RevenueBreakdown label="OS entregues" value={totals.receitaOS} total={totals.receita} color="#2563eb" />
          <RevenueBreakdown label="Vendas de peças" value={totals.receitaPeca} total={totals.receita} color="#0d9488" />
          <div className="pt-3 mt-3 border-t border-slate-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">Total</span>
            <span className="font-heading font-bold text-lg text-slate-900 tabular-nums">R$ {fmtBRL(totals.receita)}</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Por forma de pagamento</h3>
          {Object.keys(totals.byPay).length === 0 ? (
            <p className="text-sm italic text-slate-400">Sem dados no período.</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(totals.byPay).sort((a, b) => b[1] - a[1]).map(([k, v]) => {
                const pc = PAY_COLORS[k];
                const pct = totals.receita ? (v / totals.receita) * 100 : 0;
                return (
                  <div key={k}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium" style={{ color: pc.color }}>{PAY_LABELS[k]}</span>
                      <span className="tabular-nums font-semibold text-slate-800">R$ {fmtBRL(v)}</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${pct}%`, background: pc.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Despesas por categoria</h3>
          {Object.keys(totals.byCategory).length === 0 ? (
            <p className="text-sm italic text-slate-400">Nenhuma despesa cadastrada.</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(totals.byCategory).sort((a, b) => b[1] - a[1]).map(([k, v]) => {
                const cat = CAT_INFO[k] || CAT_INFO.outros;
                const Icon = cat.icon;
                return (
                  <div key={k} className="flex items-center gap-2.5 text-sm">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: cat.color + "18", color: cat.color }}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="flex-1 font-medium text-slate-700">{cat.label}</span>
                    <span className="tabular-nums font-semibold text-slate-900">R$ {fmtBRL(v)}</span>
                  </div>
                );
              })}
              <div className="pt-2 mt-2 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</span>
                <span className="font-heading font-bold text-slate-900 tabular-nums">R$ {fmtBRL(totals.despesas)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expenses management */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-semibold text-slate-900">Despesas &amp; Funcionários</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Custos fixos e variáveis da loja</p>
          </div>
          <button
            onClick={() => { setEditingExp(null); setShowExpenseForm(true); }}
            data-testid="add-expense"
            className="h-9 px-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 transition-all hover:-translate-y-[1px]"
          >
            <Plus className="w-3.5 h-3.5" /> Nova despesa
          </button>
        </div>

        {loadingExp ? (
          <div className="p-8 text-center text-sm text-slate-400">Carregando...</div>
        ) : expenses.length === 0 ? (
          <div className="p-10 text-center">
            <Building2 className="w-9 h-9 mx-auto text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">Nenhuma despesa cadastrada ainda.</p>
            <p className="text-[12px] text-slate-400 mt-1">Cadastre condomínio, funcionários, energia, aluguel etc.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left text-[10px] font-bold uppercase text-slate-500 px-4 py-2">Categoria</th>
                <th className="text-left text-[10px] font-bold uppercase text-slate-500 px-4 py-2">Nome / Descrição</th>
                <th className="text-left text-[10px] font-bold uppercase text-slate-500 px-4 py-2">Referência</th>
                <th className="text-right text-[10px] font-bold uppercase text-slate-500 px-4 py-2">Valor</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((e) => {
                const cat = CAT_INFO[e.category] || CAT_INFO.outros;
                const Icon = cat.icon;
                return (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-2.5">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: cat.color + "18", color: cat.color }}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-xs font-semibold text-slate-700">{cat.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="text-sm font-semibold text-slate-800">{e.name}</div>
                      {e.notes && <div className="text-[11px] text-slate-400">{e.notes}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {e.recurring ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                          <Calendar className="w-3 h-3" /> Recorrente / mensal
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 font-semibold">
                          <Calendar className="w-3 h-3" /> {e.monthRef || "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-heading font-bold text-slate-900 tabular-nums">
                      R$ {fmtBRL(e.amount)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => { setEditingExp(e); setShowExpenseForm(true); }}
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        data-testid={`edit-expense-${e.id}`}
                      >
                        <Save className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setConfirmDeleteExp(e)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                        data-testid={`delete-expense-${e.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showExpenseForm && (
        <ExpenseForm
          expense={editingExp}
          onClose={() => setShowExpenseForm(false)}
          onSaved={() => {
            setShowExpenseForm(false);
            refreshExpenses();
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirmDeleteExp}
        title="Excluir despesa"
        message={confirmDeleteExp ? `Excluir a despesa "${confirmDeleteExp.name}"?` : ""}
        confirmLabel="Excluir"
        onCancel={() => setConfirmDeleteExp(null)}
        onConfirm={async () => {
          const id = confirmDeleteExp.id;
          setConfirmDeleteExp(null);
          await api.delete(`/expenses/${id}`);
          refreshExpenses();
        }}
      />

      {showPartnership && (
        <PartnershipModal
          periodLabel={periodLabel}
          orders={inFilter.orders}
          sales={inFilter.sales}
          expenses={inFilter.expenses}
          onClose={() => setShowPartnership(false)}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, hint, icon: Icon, tone, primary }) {
  const tones = {
    blue: { bg: "#eff6ff", border: "#bfdbfe", text: "#1e40af", pill: "#2563eb" },
    rose: { bg: "#fef2f2", border: "#fecaca", text: "#991b1b", pill: "#dc2626" },
    amber: { bg: "#fffbeb", border: "#fde68a", text: "#854d0e", pill: "#d97706" },
    emerald: { bg: "#ecfdf5", border: "#a7f3d0", text: "#065f46", pill: "#059669" },
    violet: { bg: "#faf5ff", border: "#e9d5ff", text: "#6b21a8", pill: "#9333ea" },
  };
  const t = tones[tone] || tones.blue;
  return (
    <div
      className={`rounded-xl p-4 shadow-sm border ${primary ? "text-white" : ""}`}
      style={
        primary
          ? { background: `linear-gradient(135deg, ${t.pill}, ${t.text})`, borderColor: t.pill }
          : { background: t.bg, borderColor: t.border, color: t.text }
      }
    >
      <div className="flex items-start justify-between">
        <div className={primary ? "text-white/70" : ""}>
          <div className="text-[10px] uppercase tracking-wider font-bold">{label}</div>
          <div className={`font-heading text-2xl font-extrabold tabular-nums mt-1 ${primary ? "text-white" : ""}`}>
            R$ {fmtBRL(value)}
          </div>
          {hint && <div className={`text-[11px] mt-1 ${primary ? "text-white/70" : "opacity-60"}`}>{hint}</div>}
        </div>
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center ${primary ? "bg-white/20" : ""}`}
          style={primary ? {} : { background: t.pill + "18", color: t.pill }}
        >
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

function RevenueBreakdown({ label, value, total, color }) {
  const pct = total ? (value / total) * 100 : 0;
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-700 font-medium">{label}</span>
        <span className="tabular-nums font-semibold text-slate-900">R$ {fmtBRL(value)}</span>
      </div>
      <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

// ─── Expense Form Modal ──────────────────────────────────────────────────────
function ExpenseForm({ expense, onClose, onSaved }) {
  const isEdit = !!expense;
  const [form, setForm] = useState(
    expense || {
      category: "condominio",
      name: "",
      amount: "",
      recurring: true,
      monthRef: "",
      notes: "",
    }
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    setErr("");
    try {
      const payload = {
        ...form,
        amount: parseFloat(String(form.amount).replace(",", ".")) || 0,
        monthRef: form.recurring ? null : form.monthRef || null,
      };
      if (isEdit) {
        await api.patch(`/expenses/${expense.id}`, payload);
      } else {
        await api.post("/expenses", payload);
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-800 to-slate-900 text-white flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold">{isEdit ? "Editar despesa" : "Nova despesa"}</h3>
            <p className="text-xs text-white/70 mt-0.5">Condomínio, funcionário, energia, etc.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Categoria</label>
            <div className="grid grid-cols-4 gap-1.5 mt-1.5">
              {Object.entries(CAT_INFO).map(([k, cat]) => {
                const Icon = cat.icon;
                const active = form.category === k;
                return (
                  <button
                    key={k}
                    onClick={() => setForm({ ...form, category: k })}
                    data-testid={`exp-cat-${k}`}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-[10px] font-semibold transition-all ${
                      active
                        ? "border-blue-500 bg-blue-50 text-blue-700 -translate-y-[1px] shadow-sm"
                        : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                    }`}
                  >
                    <Icon className="w-4 h-4" style={{ color: cat.color }} />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">
              {form.category === "funcionario" ? "Nome do funcionário" : "Nome / Descrição"}
            </label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={form.category === "funcionario" ? "Ex.: João Silva" : "Ex.: Condomínio julho"}
              className="mt-1.5 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
              data-testid="exp-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Valor (R$)</label>
              <input
                inputMode="decimal"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0,00"
                className="mt-1.5 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none text-right tabular-nums font-semibold"
                data-testid="exp-amount"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Tipo</label>
              <div className="mt-1.5 grid grid-cols-2 gap-1 bg-slate-100 rounded-lg p-0.5">
                <button
                  onClick={() => setForm({ ...form, recurring: true })}
                  className={`h-9 rounded-md text-xs font-semibold ${form.recurring ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
                  data-testid="exp-recurring"
                >
                  Mensal
                </button>
                <button
                  onClick={() => setForm({ ...form, recurring: false })}
                  className={`h-9 rounded-md text-xs font-semibold ${!form.recurring ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
                  data-testid="exp-once"
                >
                  Único
                </button>
              </div>
            </div>
          </div>

          {!form.recurring && (
            <div>
              <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Mês/ano de referência</label>
              <input
                value={form.monthRef || ""}
                onChange={(e) => setForm({ ...form, monthRef: e.target.value })}
                placeholder="07/2026"
                className="mt-1.5 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
                data-testid="exp-monthref"
              />
            </div>
          )}

          <div>
            <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Observações (opcional)</label>
            <textarea
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>

          {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</div>}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200">Cancelar</button>
          <button
            onClick={submit}
            disabled={!form.name.trim() || saving}
            data-testid="exp-save"
            className="h-10 px-5 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? "Salvando..." : "Salvar despesa"}
          </button>
        </div>
      </div>
    </div>
  );
}
